import React, { useContext, useEffect, useState } from "react";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import help from "help/viewStatusSystemInformation.md";
import Request from "common/Request";
import {
  parseHardwareChassis,
  parseHardwareCpu,
  parseHardwareMemory,
  parsePktendStatisticsExtended,
  parseFilesystem,
} from './api';
import {
  atBqnTarget,
  bqnIdTarget,
  parseClusterHardwareId,
  parseClusterResponse,
  parseHostname,
  parseVersion,
  parseStrTable,
  withBqnId,
} from "common/api";
import View from "common/View";
import styled from "styled-components";

// The data of the BQN selected in the header, or of the whole cluster.
const getCommands = (mode, bqnId) =>
  [
    "show version",
    "show hardware chassis",
    "show hardware id",
    "show hardware cpu",
    "show hardware memory",
    "show system filesystem",
    "show pkteng statistics extended",
  ].map((command) => atBqnTarget(command, mode, bqnId));

// The BQN whose data a response carries when it reports no BQN ID: the one
// selected in the header, which in standalone mode is the only BQN there is.
// The whole cluster reports every BQN under its own ID, so it needs none.
const bqnIdOfData = (bqnId) => (bqnId === "all" ? undefined : bqnId);

// The names of the whole cluster are reported by "show system hostname
// detail", which takes no BQN of its own, while the name of the BQN selected
// is answered by the command that does take one.
const hostnamesRetrieval = (mode, bqnId) =>
  bqnIdTarget(mode, bqnId) === undefined
    ? ifCl.run("show system hostname detail").then(parseClusterHostname)
    : ifCl
        .run(withBqnId("show system hostname", bqnId))
        .then((response) => ({ [bqnId]: parseHostname(response) }));

const parseClusterHostname = (response) => {
  const output = {};

  parseStrTable(response, (attrs, i) => {
    output[attrs[0]] = attrs[1];
  });

  return output;
};

// A response of one BQN reports no BQN ID of its own, so the data it carries
// is the one of the BQN asked for.
function parseForBqnId(
  bqnId,
  bqnIdShown,
  hostname,
  versionResp,
  hardwareChassisResp,
  hardwareIdResp,
  hardwareCpuResp,
  hardwareMemoryResp,
  filesystemResp,
  pktendStatisticsExtendedResp,
) {
  const version = parseClusterResponse(versionResp, parseVersion, bqnIdShown);
  const chassis = parseClusterResponse(
    hardwareChassisResp,
    parseHardwareChassis,
    bqnIdShown
  );
  const hardwareId = parseClusterHardwareId(hardwareIdResp);
  const hardwareCpu = parseClusterResponse(
    hardwareCpuResp,
    parseHardwareCpu,
    bqnIdShown
  );
  const memory = parseClusterResponse(
    hardwareMemoryResp,
    parseHardwareMemory,
    bqnIdShown
  );
  const disk = parseClusterResponse(
    filesystemResp,
    parseFilesystem,
    bqnIdShown
  );
  const dropped = parsePktendStatisticsExtended(pktendStatisticsExtendedResp);

  return Object.keys(memory)
    .filter( key => bqnId === "all" || key === bqnId.toString())
    .map( id => {
      const [vendor, product, serial] = chassis[id] ||
        ['not found', 'not found', 'not found'];
      const [cpu, cores] = hardwareCpu[id] || ['unknown', 'unknown'];
      return {
        bqnId: id,
        hostname: hostname[id] || 'unknown',
        version: version[id] || 'unknown',
        vendor,
        product,
        serial,
        hardwareId: hardwareId[id] || 'unknown',
        cpu,
        cores,
        memory: memory[id],
        disk: disk[id] || 'unknown',
        dropped,
      };
    })
  }

const Item = ({ data }) => {
  const clusterStatus = useContext(BqnContext).clusterStatus;
  const mode = clusterStatus?.mode;
  return (
    <div className="row">
      {mode !== "standalone" ? <h6> BQN {data.bqnId} </h6> : null}
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div>System name</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.hostname}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div className="nowrap">BQN release</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.version}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div>Vendor</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.vendor}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div className="nowrap">Product</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.product}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div>Serial number</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.serial}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div className="nowrap">Hardware ID</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.hardwareId}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div>CPU model</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.cpu}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div className="nowrap">CPU cores</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.cores}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div>Memory size</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.memory}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div className="nowrap">Root disk partition</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-5">
          <div className="font-bold db-counters">{data.disk}</div>
        </div>
      </div>
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12 margin-b-0">
        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-6 margin-b-5">
          <div>Dropped pkts</div>
        </div>
        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-6 margin-b-25">
          <div className="font-bold db-counters">{data.dropped}</div>
        </div>
      </div>
    </div>
  );
};
const ContentLayout = styled.div`
  margin: 20px;
`;

const Content = ({ data }) => data ? (
    <ContentLayout>
      {data.map((item) => (
        <Item key={item.bqnId} data={item} />
      ))}
    </ContentLayout>
  ) : null;

const doRequest = (mode, bqnId) =>
  Promise.all([
    hostnamesRetrieval(mode, bqnId),
    ...getCommands(mode, bqnId).map(cmd => ifCl.run(cmd)),
  ]).then(([hostname, ...responses]) => ({
    result: parseForBqnId(
      bqnId,
      bqnIdOfData(bqnId),
      hostname,
      ...responses
    ),
  }));

const SystemInformation = ({returnView}) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);

  const doLoad = () => {
    setRequest(doRequest(mode, bqnId));
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad();
    return actions.recv("do-load", () => doLoad());
  }, [mode, bqnId]);

  return (
    <View title="System Information" mdHelp={help} className="container-lg" returnView={returnView}>
      {request === null ? null :
        <Request during={request}>
          {(result) => <Content data={result.result} />}
        </Request>
      }
    </View>
  );
};

export default SystemInformation;
